import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import PlatformAiChatDialog from "../components/PlatformAiChatDialog";
import { isPlatformConsolePath } from "../pages/Platform/XprobaseConsole/platformConsoleRoutes";

const PlatformAiContext = createContext(null);

function isPlatformAiShortcut(event) {
  const key = String(event.key || "").toLowerCase();
  if (key !== "i") return false;
  if (!(event.ctrlKey || event.metaKey)) return false;
  if (event.altKey) return false;
  // Evita capturar Ctrl+Shift+I (DevTools) e similares.
  if (event.shiftKey) return false;
  return true;
}

function shouldBlockShortcutTarget(event) {
  const el = event.target;
  if (!el || typeof el !== "object") return false;
  // Em inputs/contenteditable deixa o atalho nativo (itálico etc.) — mas Ctrl+I
  // no app abre o assistente; só bloqueamos se for um editor rico marcado.
  if (el.isContentEditable) return true;
  const tag = String(el.tagName || "").toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") {
    // Em campos de formulário ainda abrimos o assistente (pedido do produto),
    // exceto password.
    const type = String(el.type || "").toLowerCase();
    if (type === "password") return true;
  }
  return false;
}

export function PlatformAiProvider({ children }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const onPlatformConsole = isPlatformConsolePath(location.pathname);
  // Console master: sem assistente de tenant. Portal representante: permitido.
  const canOpen = !onPlatformConsole;

  const openPlatformAi = useCallback(() => {
    if (!canOpen) return;
    setOpen(true);
  }, [canOpen]);

  const closePlatformAi = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (!isPlatformAiShortcut(event)) return;
      if (shouldBlockShortcutTarget(event)) return;
      if (!canOpen) return;
      event.preventDefault();
      event.stopPropagation();
      if (open) return;
      openPlatformAi();
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [canOpen, open, openPlatformAi]);

  // Ao entrar no console master, fecha se estiver aberto.
  useEffect(() => {
    if (!canOpen && open) setOpen(false);
  }, [canOpen, open]);

  const value = useMemo(
    () => ({
      openPlatformAi,
      closePlatformAi,
      isPlatformAiOpen: open,
      canOpenPlatformAi: canOpen,
    }),
    [canOpen, closePlatformAi, open, openPlatformAi]
  );

  return (
    <PlatformAiContext.Provider value={value}>
      {children}
      <PlatformAiChatDialog open={open} onClose={closePlatformAi} />
    </PlatformAiContext.Provider>
  );
}

export function usePlatformAi() {
  const ctx = useContext(PlatformAiContext);
  if (!ctx) {
    throw new Error("usePlatformAi must be used within PlatformAiProvider");
  }
  return ctx;
}

/** Uso opcional (Topbar pode renderizar fora do provider em testes). */
export function usePlatformAiOptional() {
  return useContext(PlatformAiContext);
}
